/**
 * Provider Admin Service (spec 013 — Service Provider lifecycle admin)
 *
 * API client for the ServiceManager.API provider-admin endpoints
 * (ProviderAdminController — [Authorize(Roles = "Admin,SystemAdmin,PlatformAdmin")]):
 *
 *   GET  /api/providers/admin?status=&search=&page=&pageSize=&sortDescending=
 *   GET  /api/providers/admin/{spid}?auditEntries=      — detail + recent audit
 *   GET  /api/providers/admin/{spid}/audit?page=&pageSize= — paged audit trail
 *   POST /api/providers/admin/{spid}/suspend             body { reason }
 *   POST /api/providers/admin/{spid}/reactivate          (no body)
 *
 * Like agentAdminService.ts, these endpoints live on ServiceManager.API — a
 * different host than the TaskManager-backed /api routes. The base URL is
 * injected at build time via VITE_SERVICEMANAGER_API_URL; when unset (local
 * dev) paths stay relative and the Vite /api/providers proxy (vite.config.ts)
 * routes them.
 *
 * Error contract: 404 → { error: "..." } (provider not found), 409 →
 * { error: "..." } (invalid state transition, e.g. suspending an already
 * suspended provider). AccountStatus serializes as a string
 * ("Active" | "Suspended") via JsonStringEnumConverter.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

// ---------------------------------------------------------------------------
// Base URL — ServiceManager.API host (build-time), relative in local dev
// ---------------------------------------------------------------------------

const SERVICEMANAGER_BASE: string = import.meta.env.VITE_SERVICEMANAGER_API_URL || '';

const PROVIDERS_ADMIN = `${SERVICEMANAGER_BASE}/api/providers/admin`;

// ---------------------------------------------------------------------------
// Response shapes — aligned to ServiceManager.Models.Payloads.ProviderAdmin
// (System.Text.Json camelCase)
// ---------------------------------------------------------------------------

/** ProviderAccountStatus enum — serialized as string by the API. */
export type ProviderAccountStatus = 'Active' | 'Suspended';

/** One provider row from GET /api/providers/admin (ProviderAdminListItemDto). */
export interface ProviderAdminListItem {
  spid: number;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  verificationStatus: string | null;
  onboardingStatus: number | null;
  accountStatus: ProviderAccountStatus;
  suspendedAt: string | null;
  suspendedReason: string | null;
  isDeleted: boolean;
  isReferralAgent: boolean;
  createdAt: string;
}

/** Paged provider list envelope (ProviderAdminListResponse). */
export interface ProviderAdminListResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  items: ProviderAdminListItem[];
}

/** One append-only audit trail row (ProviderAuditEntryDto). */
export interface ProviderAuditEntry {
  auditLogId: number;
  spid: number;
  action: string;
  actorType: string | null;
  actorId: string | null;
  details: string | null;
  createdAt: string;
}

/** Provider detail + most recent audit entries (ProviderAdminDetailDto). */
export interface ProviderAdminDetail extends ProviderAdminListItem {
  recentAudit: ProviderAuditEntry[];
}

/** Paged audit trail envelope (ProviderAuditListResponse). */
export interface ProviderAuditListResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  items: ProviderAuditEntry[];
}

export interface GetProvidersParams {
  /** Filter by account status; omit for all providers. */
  status?: ProviderAccountStatus;
  /** Company name or email substring. */
  search?: string;
  page?: number;
  pageSize?: number;
  /** Sorted by CreatedAt — newest first by default. */
  sortDescending?: boolean;
}

/** Thrown on 404 — the SPID was not found. */
export class ProviderNotFoundError extends Error {
  constructor(spid: number) {
    super(`Provider not found: ${spid}`);
    this.name = 'ProviderNotFoundError';
  }
}

/** Thrown on 409 — invalid lifecycle transition (already suspended / not suspended). */
export class ProviderConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProviderConflictError';
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

async function readErrorMessage(response: Response): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  return (
    (body['error'] as string | undefined) ??
    (body['message'] as string | undefined) ??
    `HTTP ${response.status}: ${response.statusText}`
  );
}

async function callApi<T>(spid: number | null, url: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    if (response.status === 404 && spid !== null) {
      throw new ProviderNotFoundError(spid);
    }
    const message = await readErrorMessage(response);
    if (response.status === 409) {
      throw new ProviderConflictError(message);
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Paged, cross-tenant provider list. Filters: status (Active/Suspended),
 * search (company name or email substring). Newest first by default.
 */
export function getProviders(params: GetProvidersParams = {}): Promise<ProviderAdminListResponse> {
  const qs = buildQuery({
    status: params.status,
    search: params.search,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 25,
    sortDescending: params.sortDescending,
  });
  return callApi<ProviderAdminListResponse>(null, `${PROVIDERS_ADMIN}${qs}`);
}

/** Provider detail + the most recent audit entries (default 10, max 100). */
export function getProviderDetail(
  spid: number,
  auditEntries = 10
): Promise<ProviderAdminDetail> {
  return callApi<ProviderAdminDetail>(
    spid,
    `${PROVIDERS_ADMIN}/${spid}${buildQuery({ auditEntries })}`
  );
}

/** Paged audit trail for one provider, newest first. */
export function getProviderAudit(
  spid: number,
  page = 1,
  pageSize = 25
): Promise<ProviderAuditListResponse> {
  return callApi<ProviderAuditListResponse>(
    spid,
    `${PROVIDERS_ADMIN}/${spid}/audit${buildQuery({ page, pageSize })}`
  );
}

/**
 * Suspend the provider — AccountStatus=Suspended, audit row, immediate
 * enforcement on the provider's next request. 409 if already suspended.
 */
export function suspendProvider(spid: number, reason: string): Promise<ProviderAdminDetail> {
  return callApi<ProviderAdminDetail>(spid, `${PROVIDERS_ADMIN}/${spid}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason.trim() }),
  });
}

/**
 * Clear a suspension — AccountStatus=Active, SuspendedAt/Reason nulled,
 * audit row. 409 if the provider is not suspended.
 */
export function reactivateProvider(spid: number): Promise<ProviderAdminDetail> {
  return callApi<ProviderAdminDetail>(spid, `${PROVIDERS_ADMIN}/${spid}/reactivate`, {
    method: 'POST',
  });
}

export const providerAdminService = {
  getProviders,
  getProviderDetail,
  getProviderAudit,
  suspendProvider,
  reactivateProvider,
};
